import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { loadRedirects, matchRedirect, type RedirectRule } from './lib/data.ts';
import { usePageMeta } from './lib/usePageMeta.ts';

/*
 * The router's own small components, kept out of main.tsx so that file exports nothing but the
 * bootstrap: a module that both mounts the app and defines components loses Fast Refresh for
 * them, which is what react-refresh/only-export-components points at.
 */

export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname, hash]);
  return null;
}

export function NotFound() {
  // The server already answered 404 on the first load; this keeps the tab honest after an in-app
  // navigation, and keeps the page out of search results either way.
  usePageMeta({
    title: 'Siden findes ikke',
    description: 'Vi har fået ny hjemmeside, og den side du ledte efter findes ikke længere på samme adresse.',
    noIndex: true,
  });

  const links = [
    { href: '/', label: 'Forsiden' },
    { href: '/program', label: 'Program' },
    { href: '/nyheder', label: 'Nyheder' },
    { href: '/praktisk', label: 'Praktisk info' },
  ];
  return (
    <div className="min-h-screen flex items-center justify-center bg-mf-yellow p-10 text-center">
      <div className="max-w-xl">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-mf-coral mb-4">
          404 · siden findes ikke
        </div>
        <h1 className="font-display text-mf-black text-5xl md:text-7xl leading-[0.95] mb-6">
          Velkommen —<br />men siden er flyttet
        </h1>
        <p className="text-mf-black/80 mb-8 leading-relaxed">
          Vi har fået ny hjemmeside, og den side du ledte efter findes ikke længere på samme adresse.
          Der er ikke sket noget galt — lad os hjælpe dig videre.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          {links.map(({ href, label }) => (
            <a
              key={href}
              href={href}
              className="inline-flex items-center gap-2 bg-mf-green text-mf-yellow rounded-full px-6 py-3 font-medium hover:bg-mf-coral hover:text-white transition-colors"
            >
              {label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Catch-all: an unknown path might be an old-site URL we redirect (redirects.json), so resolve that
 * before deciding it is a 404. Renders nothing until the (tiny, cached) redirect list has loaded, so
 * a real old link lands on its new page without first flashing the 404.
 */
export function RedirectOrNotFound() {
  const { pathname, search } = useLocation();
  const [rules, setRules] = useState<RedirectRule[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadRedirects().then(r => { if (!cancelled) setRules(r); });
    return () => { cancelled = true; };
  }, []);

  if (rules === null) return null;

  const to = matchRedirect(pathname + search, rules);
  if (to) {
    if (/^https?:\/\//i.test(to)) {
      window.location.replace(to);
      return null;
    }
    return <Navigate to={to} replace />;
  }
  return <NotFound />;
}